/**
 * client/src/components/layout/ladder-downgrade-push-listener.tsx
 *
 * Mounts the ladder-downgrade listener once, inside the authenticated app
 * shell, so a person whose session was reduced is told so on whatever page
 * they happen to be on (Task 1b Step 4, AC-45) — not only if they are
 * already looking at Security Centre.
 *
 * Two effects on arrival:
 *   1. Invalidate the queries that read the session's current standing —
 *      `["security/posture"]` (the reading `SessionAccessStrip` and the
 *      reveal panels read) and `AUTH_ME_QUERY_KEY` (the session's `aal`) —
 *      so both re-read after a critical downgrade instead of staying stale
 *      until the next unrelated refetch.
 *   2. Raise a toast with the server's `message`, VERBATIM — `toast.notice`,
 *      never `toast`/`toast.success`, because a downgrade is neither a user
 *      error (amber) nor a success (emerald is reserved). No template, no
 *      interpolation, no level formatting of its own: the client adds
 *      nothing to the server's copy (AC-45's client half — see
 *      `use-ladder-downgrade-push.ts` header for the server-owned-copy
 *      contract).
 *
 * Renders nothing. A WebSocket notice reaches a LIVE session only, so it can
 * never be the thing a person relies on — the gate that asks the holder to
 * verify is. Nothing here claims delivery.
 */

import { useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/ui/toast";
import { AUTH_ME_QUERY_KEY } from "@/lib/auth-me";
import { useLadderDowngradePush } from "@/lib/use-ladder-downgrade-push";

export function LadderDowngradePushListener(): null {
  const queryClient = useQueryClient();

  useLadderDowngradePush((frame) => {
    void queryClient.invalidateQueries({ queryKey: ["security/posture"] });
    void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    toast.notice(frame.message);
  });

  return null;
}
